import { useState, useEffect, useCallback } from 'react'
import { supabase } from '../lib/supabase'
import { listTransactions, createTransaction, updateTransaction, updateInstallmentGroup, deleteTransaction, deleteInstallmentGroup, amortizeInstallments as amortizeInstallmentsService, toggleRealized as toggleRealizedService, markInvoiceAsPaid as markInvoiceAsPaidService } from '../services/transactionService'
import type { Transaction, TransactionFormData } from '../types'

export function useTransactions(
  householdId: string | null,
  month: number,
  year: number,
) {
  const [transactions, setTransactions] = useState<Transaction[]>([])
  const [loading, setLoading]           = useState(false)
  const [error, setError]               = useState<string | null>(null)

  const fetch = useCallback(async () => {
    if (!householdId) return
    setLoading(true)
    setError(null)
    try {
      setTransactions(await listTransactions(householdId, month, year))
    } catch (err) {
      setError((err as Error).message)
    } finally {
      setLoading(false)
    }
  }, [householdId, month, year])

  // Initial fetch + realtime subscription
  useEffect(() => {
    fetch()
    if (!householdId) return

    const channel = supabase
      .channel(`tx-${householdId}-${year}-${month}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'transactions',
          filter: `household_id=eq.${householdId}`,
        },
        () => fetch(),
      )
      .subscribe()

    return () => { supabase.removeChannel(channel) }
  }, [fetch, householdId, year, month])

  async function addTransaction(userId: string, data: TransactionFormData): Promise<void> {
    if (!householdId) return
    await createTransaction(householdId, userId, data)
    await fetch()
  }

  async function editTransaction(id: string, data: TransactionFormData): Promise<void> {
    await updateTransaction(id, data)
    await fetch()
  }

  async function removeTransaction(id: string): Promise<void> {
    await deleteTransaction(id)
    await fetch()
  }

  async function editInstallmentGroup(
    userId: string,
    groupId: string,
    currentDate: string,
    currentIndex: number,
    data: TransactionFormData,
  ): Promise<void> {
    if (!householdId) return
    await updateInstallmentGroup(householdId, userId, groupId, currentDate, currentIndex, data)
    await fetch()
  }

  async function removeInstallmentGroup(groupId: string): Promise<void> {
    await deleteInstallmentGroup(groupId)
    await fetch()
  }

  async function amortizeInstallments(groupId: string, currentIndex: number, targetDate: string, count: number): Promise<void> {
    await amortizeInstallmentsService(groupId, currentIndex, targetDate, count)
    await fetch()
  }

  async function toggleRealized(id: string, currentRealized: boolean): Promise<void> {
    await toggleRealizedService(id, !currentRealized)
    await fetch()
  }

  async function markInvoiceAsPaid(cardId: string): Promise<void> {
    const unpaidIds = transactions
      .filter(t => t.card_id === cardId && !t.realized)
      .map(t => t.id)
    await markInvoiceAsPaidService(unpaidIds)
    await fetch()
  }

  return { transactions, loading, error, addTransaction, editTransaction, editInstallmentGroup, removeTransaction, removeInstallmentGroup, amortizeInstallments, toggleRealized, markInvoiceAsPaid, refetch: fetch }
}
